import { StartApplication } from '@signageos/actions/dist/Application/applicationActions';
import { PlatformConnect } from '@signageos/actions/dist/Socket/socketActions';
import wait from '@signageos/lib/dist/Timer/wait';
import Debug from 'debug';
import { put, takeEvery } from 'redux-saga/effects';
import { injectDependencies } from '../../DI/dependencyInjection';
import { DEBUG_CHECK_INTERVAL_MS } from '../../Debug/debugConstants';
import IConfig, { SubscriptionType } from '../../Display/IConfig';
import IBasicDriver from '../../NativeDevice/IBasicDriver';
import { IFrontManagementDriver } from '../../NativeDevice/Management/IManagementDriver';
import Property from '../../Property/Property';
import { IPropertyStorage } from '../../Property/propertyStorage';
import {
	ChangeSubscriptionToOpen,
	ChangeSubscriptionToPlatform,
	ConfigurationChanged,
	ConfigurationInjected,
	IOptionalConfig,
} from './deviceConfigurationActions';
import { checkDeviceConfigurationSaga, checkDeviceConfigurationSagaManual } from './deviceConfigurationCheckerSaga';
import { getConfigurationBaseUrl } from './deviceConfigurationHelper';
import { areTelemetryIntervalsSame } from './telemetryIntervals';
import { DEBOUNCE } from '../../Intervals/constants';
import { SocketName } from '../../Socket/socketSagas';
import Responsibility from '../../Feature/Responsibility';
import { IResponsibilities } from '../../Feature/Responsibilities';
import { Dispatch } from 'redux';

const debug = Debug('@signageos/front-display:Device:Configuration:deviceConfigurationSagas');

export function* fetchingConfiguration(
	getNativeDriver: () => IBasicDriver,
	getManagementDriver: () => IFrontManagementDriver,
	responsibilities: IResponsibilities,
	defaultConfig: IConfig,
	publicKey: string,
	threadName: SocketName,
	dispatch: Dispatch<unknown>,
) {
	yield takeEvery(StartApplication, function* () {
		const overidenDefaultConfig = { ...defaultConfig };
		const isEnabled: boolean = yield getManagementDriver().isDebugEnabled();
		if (isEnabled) {
			// Debug checking is enabled after restart
			overidenDefaultConfig.checkInterval = DEBUG_CHECK_INTERVAL_MS;
		}
		if (responsibilities.has(Responsibility.SOCKET_CONFIG)) {
			const deviceUid: string = yield getNativeDriver().getDeviceUid();
			const getConfigUrl = () => getConfigurationBaseUrl(getNativeDriver(), defaultConfig);
			yield* checkDeviceConfigurationSaga(deviceUid, overidenDefaultConfig, getConfigUrl, publicKey, undefined, threadName);
		} else {
			yield* checkDeviceConfigurationSagaManual(overidenDefaultConfig, threadName);
			const callback = (config: IOptionalConfig) => {
				dispatch({ type: ConfigurationInjected, config });
			};
			getNativeDriver().requestConfiguration(callback);
		}
	});
}

export function* updatingConfiguration(propertyStorage: IPropertyStorage, defaultConfig: IConfig, threadName: SocketName) {
	let lastConfig: IOptionalConfig<string | null> = {
		updatedAt: null,
		platformUri: null,
		socketDriver: null,
		staticBaseUrl: null,
		uploadBaseUrl: null,
		weinreUri: null,
		extendedManagementUrl: null,
		subscriptionType: null,
		checkInterval: null,
		telemetryIntervals: null,
		cacheInvalidationInterval: null,
		checkTimeBeforeConnection: false,
		offlineActionsLimits: null,
		featureFlags: null,
		screenshotRotationPortrait: null,
		telemetryInvalidatedAt: null,
	};
	yield takeEvery(StartApplication, function* () {
		yield initiateSubscriptionType(propertyStorage, lastConfig, defaultConfig.subscriptionType);
	});
	yield takeEvery(ConfigurationChanged, function* ({ config }: ConfigurationChanged) {
		debug(threadName, 'new configuration', config);
		if (config.staticBaseUrl && config.staticBaseUrl !== lastConfig.staticBaseUrl) {
			yield injectDependencies({
				staticBaseUrl: config.staticBaseUrl,
			});
		}
		if (config.uploadBaseUrl && config.uploadBaseUrl !== lastConfig.uploadBaseUrl) {
			yield injectDependencies({
				uploadBaseUrl: config.uploadBaseUrl,
			});
		}
		// Always inject telemetry intervals if they have changed, including from null to defaults
		if (!areTelemetryIntervalsSame(config.telemetryIntervals, lastConfig.telemetryIntervals)) {
			yield injectDependencies({
				telemetryIntervals: config.telemetryIntervals,
			});
		}
		if (config.cacheInvalidationInterval && config.cacheInvalidationInterval !== lastConfig.cacheInvalidationInterval) {
			yield injectDependencies({
				cacheInvalidationInterval: config.cacheInvalidationInterval,
			});
		}
		if (config.offlineActionsLimits && config.offlineActionsLimits !== lastConfig.offlineActionsLimits) {
			yield injectDependencies({
				offlineActionsLimits: config.offlineActionsLimits,
			});
		}
		if (config.weinreUri !== lastConfig.weinreUri) {
			yield propertyStorage.setValue(Property.DEBUG_WEINRE_SERVER_URL, config.weinreUri);
		}
		if (config.subscriptionType !== lastConfig.subscriptionType) {
			yield putSubscriptionType(config.subscriptionType || defaultConfig.subscriptionType);
			yield propertyStorage.setValue(Property.INITIAL_SUBSCRIPTION_TYPE, config.subscriptionType);
		}
		// Always inject feature flags if they have changed, including from null to defaultFeatureFlags
		if (config.featureFlags !== lastConfig.featureFlags) {
			yield injectDependencies({
				featureFlags: config.featureFlags,
			});
		}
		if (config.screenshotRotationPortrait !== lastConfig.screenshotRotationPortrait) {
			yield injectDependencies({
				screenshotRotationPortrait: config.screenshotRotationPortrait,
			});
		}
		if (config.telemetryInvalidatedAt !== lastConfig.telemetryInvalidatedAt) {
			yield injectDependencies({
				telemetryInvalidatedAt: config.telemetryInvalidatedAt,
			});
		}
		if (
			config.platformUri !== lastConfig.platformUri ||
			config.socketDriver !== lastConfig.socketDriver ||
			config.updatedAt !== lastConfig.updatedAt ||
			config.subscriptionType !== lastConfig.subscriptionType ||
			config.checkTimeBeforeConnection !== lastConfig.checkTimeBeforeConnection
		) {
			yield put({
				type: PlatformConnect,
				platformUri: config.platformUri,
				driver: config.socketDriver,
				checkTimeBeforeConnection: config.checkTimeBeforeConnection,
				trigger: 'Configuration changed',
			} as PlatformConnect);
		}
		lastConfig = config;
	});
}

function* putSubscriptionType(subscriptionType: SubscriptionType) {
	yield injectDependencies({
		subscriptionType: subscriptionType,
	});
	switch (subscriptionType) {
		case SubscriptionType.platform:
			yield put({ type: ChangeSubscriptionToPlatform } as ChangeSubscriptionToPlatform);
			break;
		case SubscriptionType.open:
		default:
			yield put({ type: ChangeSubscriptionToOpen } as ChangeSubscriptionToOpen);
	}
}

/** lastConfig is mutated in key subscriptionType */
function* initiateSubscriptionType(
	propertyStorage: IPropertyStorage,
	lastConfig: IOptionalConfig<string | null>,
	defaultSubscriptionType: SubscriptionType,
) {
	const initialSubscriptionType: SubscriptionType | null = yield propertyStorage.getValueOrDefault(
		Property.INITIAL_SUBSCRIPTION_TYPE,
		null,
	);
	// TODO Remove the artificial delay. Until then:
	// This MUST NOT emit subscription type before all `runPeriodicTaskSagaWhile*` calls get a chance to register callbacks.
	yield wait(DEBOUNCE);
	yield putSubscriptionType(initialSubscriptionType || defaultSubscriptionType);
	lastConfig.subscriptionType = initialSubscriptionType;
}
